import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { authAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Separator } from '../components/ui/separator';
import { Eye, EyeOff, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;

// Consulate staff always land in the consulate directory, never the member dashboard,
// whatever page sent them to log in; nobody else is sent into the consulate area.
const destinationFor = (userData, from) => {
  if (userData?.role === 'consulate') {
    return from.startsWith('/consulate') ? from : '/consulate';
  }
  return from.startsWith('/consulate') ? '/dashboard' : from;
};

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login, setUserData } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const googleBtnRef = useRef(null);

  const from = location.state?.from || '/dashboard';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const userData = await login(email, password);
      toast.success(userData?.role === 'consulate' ? `Welcome, ${userData.name}` : 'Welcome back!');
      navigate(destinationFor(userData, from), { replace: true });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!GOOGLE_CLIENT_ID || !googleBtnRef.current) return;
    let cancelled = false;
    const tryRender = () => {
      if (cancelled) return;
      const g = window.google;
      if (!g?.accounts?.id) {
        setTimeout(tryRender, 200);
        return;
      }
      g.accounts.id.initialize({
        client_id: GOOGLE_CLIENT_ID,
        callback: async ({ credential }) => {
          if (!credential) return;
          setLoading(true);
          try {
            const res = await authAPI.googleAuth(credential);
            const { token, ...userData } = res.data;
            localStorage.setItem('token', token);
            setUserData(userData);
            toast.success('Welcome!');
            navigate(destinationFor(userData, from), { replace: true });
          } catch (err) {
            toast.error(err.response?.data?.detail || 'Google sign-in failed');
          } finally {
            setLoading(false);
          }
        },
        ux_mode: 'popup',
      });
      googleBtnRef.current.innerHTML = '';
      g.accounts.id.renderButton(googleBtnRef.current, {
        type: 'standard',
        theme: 'outline',
        size: 'large',
        text: 'continue_with',
        shape: 'rectangular',
        logo_alignment: 'left',
        width: googleBtnRef.current.offsetWidth || 320,
      });
    };
    tryRender();
    return () => { cancelled = true; };
  }, [from, navigate, setUserData]);

  return (
    <div className="min-h-screen flex">
      {/* Left side - Image */}
      <div 
        className="hidden lg:flex lg:w-1/2 relative"
        style={{
          backgroundImage: 'url(/images/login-bg.jpg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="absolute inset-0 bg-ags-blue/70" />
        <div className="relative z-10 p-12 flex flex-col justify-between">
          <Link to="/" className="flex items-center space-x-2">
            <img 
              src="/agshk-logo-transparent.png" 
              alt="AGS-HK Logo" 
              className="w-10 h-10 rounded-sm object-contain bg-white"
            />
            <span className="font-heading font-bold text-white text-xl">AGS-HK</span>
          </Link>
          <div>
            <h2 className="font-heading text-3xl text-white mb-4">
              Welcome Back
            </h2>
            <p className="text-white/80 max-w-sm">
              Log in to access your member dashboard, connect with fellow scholars, 
              and stay updated on community events.
            </p>
          </div>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-ags-bg">
        <div className="w-full max-w-md">
          {/* Mobile logo */}
          <div className="lg:hidden mb-8 text-center">
            <Link to="/" className="inline-flex items-center space-x-2">
              <img 
                src="/agshk-logo-transparent.png"
                alt="AGS-HK Logo" 
                className="w-10 h-10 rounded-sm object-contain"
              />
              <span className="font-heading font-bold text-ags-blue text-xl">AGS-HK</span>
            </Link>
          </div>

          <Card className="border-slate-200 shadow-sm">
            <CardHeader className="text-center">
              <CardTitle className="font-heading text-2xl">Log In</CardTitle>
              <CardDescription>
                Access your AGS-HK member account
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="your.email@university.hk"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    data-testid="login-email-input"
                  />
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="password">Password</Label>
                    <Link to="/forgot-password" className="text-xs text-ags-blue hover:underline">
                      Forgot password?
                    </Link>
                  </div>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Enter your password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      data-testid="login-password-input"
                    />
                    <button
                      type="button"
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-ags-text-muted hover:text-ags-text"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                <Button 
                  type="submit" 
                  className="w-full bg-ags-blue hover:bg-ags-blue/90"
                  disabled={loading}
                  data-testid="login-submit-btn"
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  Log In
                </Button>
              </form>

              <div className="relative my-6">
                <Separator />
                <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-white px-2 text-xs text-ags-text-muted">
                  OR
                </span>
              </div>

              {GOOGLE_CLIENT_ID ? (
                <div ref={googleBtnRef} className="w-full flex justify-center" data-testid="google-login-btn" />
              ) : null}

              <div className="mt-6 text-center text-sm text-ags-text-secondary">
                Don't have an account?{' '}
                <Link to="/register" className="text-ags-blue hover:underline font-medium">
                  Register
                </Link>
              </div>
              <div className="mt-2 text-center text-sm text-ags-text-secondary">
                <Link to="/apply" className="text-ags-blue hover:underline font-medium">
                  Apply for Membership
                </Link>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Login;
